import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listLinks, createLink, deleteLink, listTasks, ApiError } from '../api';
import { Select } from './Select';
import { useProjectRole } from '../roles';

/**
 * Creating a link between two tasks.
 *
 * `createLink` has existed in the API and the client since Phase 1 with no caller, so the
 * blocked/blocking relationship could only be made over the API or MCP — while the shortlist
 * already refused to offer a blocked task, the day planner already skipped one, and the task
 * detail already listed links it gave you no way to create. Sequencing is how a large goal
 * actually gets planned: "I cannot book the trains until the dates are agreed" is the shape of
 * most real dependencies.
 *
 * Searching rather than a dropdown of everything: a task list grows without bound and a select
 * of four hundred titles is not a picker.
 */
function AddLink({
  taskId,
  existingIds,
  onDone,
}: {
  taskId: string;
  /** Already linked, so the same pair cannot be offered twice. */
  existingIds: string[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'blocks' | 'relates'>('blocks');
  const [error, setError] = useState<string | null>(null);

  const matches = useQuery({
    queryKey: ['tasks', { q: query, forLink: true }],
    queryFn: () => listTasks({ q: query }),
    enabled: query.trim().length >= 2,
  });

  const add = useMutation({
    mutationFn: (otherId: string) => createLink(taskId, otherId, type),
    onSuccess: () => {
      setQuery('');
      setError(null);
      onDone();
      void queryClient.invalidateQueries();
    },
    // The interesting failure is the cycle check, and its message explains the consequence
    // ("leaving the tasks unschedulable") rather than naming a rule. Worth showing verbatim.
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not add that link.'),
  });

  const offered = (matches.data ?? []).filter(
    (candidate) => candidate.id !== taskId && !existingIds.includes(candidate.id),
  );

  return (
    <div className="add-link">
      <div className="row gap">
        <label className="subtext">
          This task
          <Select
            options={[
              { value: 'blocks', label: 'blocks…' },
              { value: 'relates', label: 'relates to…' },
            ]}
            value={type}
            onChange={(next) => next && setType(next as 'blocks' | 'relates')}
            ariaLabel="Link type"
            allowEmpty={false}
            compact
          />
        </label>
        <input
          value={query}
          placeholder="Find a task"
          aria-label="Find a task to link"
          onChange={(e) => {
            setQuery(e.target.value);
            setError(null);
          }}
        />
      </div>

      {query.trim().length >= 2 && (
        <ul className="plain link-matches" data-testid="link-matches">
          {matches.isPending && <li className="muted">Searching…</li>}
          {!matches.isPending && offered.length === 0 && (
            <li className="muted">Nothing else matches that.</li>
          )}
          {offered.slice(0, 6).map((candidate) => (
            <li key={candidate.id}>
              <button
                className="link"
                disabled={add.isPending}
                onClick={() => add.mutate(candidate.id)}
              >
                {candidate.title}
              </button>
              {candidate.status === 'done' && <span className="pill">done</span>}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="error" data-testid="link-error">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A task's links, and the controls to change them.
 *
 * Extracted for the reason `Checklist` and `Attachments` were: the full task view had a *listing*
 * of links with no way to add or remove one, so the deeper surface could do less than the
 * shallower one — while the panel beside it had both. Two renderings of one thing drift, and the
 * one nobody is looking at drifts first.
 */
export function Links({
  taskId,
  projectId,
  onOpenTask,
  startOpen = false,
}: {
  taskId: string;
  /** The task's project, or null for personal work. Required — see `Subtasks`. */
  projectId: string | null;
  onOpenTask?: (id: string) => void;
  /** Asked for from the offer in *More* — see `Checklist` in `TaskDetail.tsx`. */
  startOpen?: boolean;
}) {
  const queryClient = useQueryClient();
  const [linking, setLinking] = useState(startOpen);
  const links = useQuery({ queryKey: ['links', taskId], queryFn: () => listLinks(taskId) });
  const unlink = useMutation({
    mutationFn: (linkId: string) => deleteLink(taskId, linkId),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  /*
    `assign` for both, drawing and erasing, which is what `linkTasks` and `unlinkTasks` charge —
    a link records a dependency two people found rather than changing what either task is. So a
    developer keeps this section whole and a reporter gets none of it. One level for the pair
    deliberately: a gate that opened drawing but not erasing would leave a mistake nobody could
    take back, which is the argument `unlinkTasks` already carries.
  */
  const mayLink = useProjectRole(projectId).can('assign');

  /* Three lines — a heading, a control and "No links." — on every task that has none. Same
     argument as the Description and Attachments panels beside it. */
  if (links.data?.length === 0 && !linking) {
    return (
      <p className="panel-empty">
        <button
          className="link"
          onClick={() => setLinking(true)}
          data-testid="add-link"
          disabled={!mayLink}
        >
          + a link
        </button>
      </p>
    );
  }

  return (
    <div className="panel">
      <div className="row gap">
        <h3>Links</h3>
        <button className="link" onClick={() => setLinking((v) => !v)} disabled={!mayLink}>
          {linking ? 'cancel' : '+ link'}
        </button>
      </div>
      {linking && (
        <AddLink
          taskId={taskId}
          existingIds={(links.data ?? []).map((l) => l.otherTaskId)}
          onDone={() => setLinking(false)}
        />
      )}
      {links.data?.length === 0 && !linking && <p className="muted">No links.</p>}
      {/* Named, because the activity thread below is a list of `li` too, and since Phase 54 its
          entries spell out the linked task's title — so "is this task still linked" stopped being
          answerable by looking for the title anywhere in the view. */}
      <ul data-testid="task-links">
        {links.data?.map((l) => (
          <li key={l.id}>
            <span className="pill">
              {l.direction === 'outgoing'
                ? l.type === 'blocks'
                  ? 'blocks'
                  : 'relates to'
                : l.type === 'blocks'
                  ? 'blocked by'
                  : 'related to'}
            </span>{' '}
            {/* Clickable, for the same reason the banner is: a link that names a task and will
                not open it is the complaint the banner already earned. */}
            <button className="link" onClick={() => onOpenTask?.(l.otherTaskId)}>
              {l.otherTaskTitle}
            </button>
            {/* Erasing answers to the same level as drawing — see above. */}
            <button className="link danger" onClick={() => unlink.mutate(l.id)} disabled={!mayLink}>
              ×
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
